import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {KatButton, KatSpinner} from "@amzn/katal-react";
import sessionStore from "src/app/store/SessionStore";
import {SessionParamUtil} from "src/app/util/SessionParamUtil";
import CaseActivityTable from "src/app/component/management/CaseActivityTable";
import ActivitySearch, {ActivitySearchCriteria} from "src/app/component/management/ActivitySearch";
import FilterBar, {caseAuditDefaultFilterValues, ALL_VALUE} from "src/app/component/common/FilterBar";
import {CaseResult} from "src/app/model/lighthouse/ManagementModels";
import {QueryCondition} from "src/app/model/QueryCondition";
import {SearchCaseRequest} from "src/app/api/model/request/SearchCaseRequest";
import {FilterOptionsPayload} from "src/app/api/model/response/FilterOptionsResponse";
import {capabilitiesFor, RoleCapabilities} from "src/app/config/RoleConfig";
import {LighthouseApi} from "src/app/api/LighthouseApi";
import {CASE_AUDIT_PAGE_SIZE, MAX_BULK_SELECTION} from "src/app/constant/Constants";
import BulkActionDialog from "src/app/component/dialog/BulkActionDialog";

import CaseViewerModal from "src/app/component/lighthouse/common/CaseViewerModal";
import {startOfDayEpoch, endOfDayEpoch} from "src/app/util/TimezoneUtil";

const EMPTY_CRITERIA: ActivitySearchCriteria = {login: '', caseId: '', dpId: ''};

// Maximum number of cases a Manager can select for a Bulk Action.
const MAX_SELECTION = MAX_BULK_SELECTION;

/**
 * Case Audit (Management) page. One call to LighthouseApi.searchCases (POST
 * /lighthouse/cases/search) returns the caller's role/alias, the server-driven `features`
 * map, the case summaries, and bidirectional page tokens. The client renders permissions
 * from `features` (the URL-param role only seeds the pre-first-response default so the page
 * doesn't flash); per-row actions render verbatim from each case's `availableActions`.
 * The View / Re-open flows load full detail via LighthouseApi.getReview.
 *
 * Pagination is token-driven (no totalCount): Next echoes `nextToken`, Previous echoes
 * `prevToken`; any filter change or Reset drops the token back to the first page.
 *
 * The Overview tab has two filter sections that both feed the single searchCases call:
 *   - ActivitySearch: Login / Case ID / DP ID.
 *   - FilterBar (shared with ReviewExport): Reference SubType, Case Type, Case SubType,
 *     Country, Review Status, Review Type, Date Range — same options/defaults as the review page.
 */
const CaseAudit = () => {
    const [session] = useState(() => {
        const context = SessionParamUtil.parse();
        sessionStore.hydrate(context);
        return context;
    });

    // Server-driven identity + capabilities (seeded from session as a pre-response fallback).
    const [features, setFeatures] = useState<RoleCapabilities>(() => capabilitiesFor(session.role));
    const [alias, setAlias] = useState<string>(session.alias);

    // Search criteria: activity fields + the shared case filters (same defaults as ReviewExport).
    const [criteria, setCriteria] = useState<ActivitySearchCriteria>(EMPTY_CRITERIA);
    const [filters, setFilters] = useState<QueryCondition>(() => caseAuditDefaultFilterValues());
    const [resetKey, setResetKey] = useState<number>(0);
    // Row selection persists across pages/searches (parent-owned so it survives table remounts).
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Results + token pagination. The server is bidirectional: it returns nextToken/prevToken
    // per page and Next/Previous just echo them back — no offset or token-history stack.
    const [cases, setCases] = useState<CaseResult[]>([]);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [prevToken, setPrevToken] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    // Token used for the page currently on screen — lets bulk-success refresh the same page.
    const currentTokenRef = useRef<string | undefined>(undefined);

    // useCallback: these feed the table's columns config — unstable identities would rebuild
    // the columns and reset KatDataTable's sort state (see CaseActivityTable).
    const onToggleSelect = useCallback((caseId: string) => setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(caseId)) {
            next.delete(caseId);
            return next;
        }
        if (next.size >= MAX_SELECTION) {
            alert(`You can select at most ${MAX_SELECTION} cases.`);
            return prev;
        }
        next.add(caseId);
        return next;
    }), []);
    const onToggleAll = useCallback(() => setSelectedIds(prev => {
        const next = new Set(prev);
        const pageIds = cases.map(c => c.caseId);
        if (pageIds.every(id => next.has(id))) {
            pageIds.forEach(id => next.delete(id));   // whole page already selected -> clear it
            return next;
        }
        let capped = false;
        for (const id of pageIds) {
            if (next.has(id)) continue;
            if (next.size >= MAX_SELECTION) {
                capped = true;
                break;
            }
            next.add(id);
        }
        if (capped) alert(`You can select at most ${MAX_SELECTION} cases.`);
        return next;
    }), [cases]);

    const buildRequest = (a: ActivitySearchCriteria, f: QueryCondition,
                          pageToken?: string): SearchCaseRequest => {
        // "ALL" (and empty) means the filter is unset -> omit it from the request.
        const pick = (v?: string) => (v && v !== ALL_VALUE) ? v : undefined;
        return {
            alias: a.login.trim() || undefined,   // meaningful for QA/Manager; server locks for auditors
            caseId: a.caseId.trim() || undefined,
            dpId: a.dpId.trim() || undefined,
            caseType: pick(f.caseType),
            skill: pick(f.caseSubType),
            country: pick(f.country),
            status: pick(f.reviewStatus),
            auditType: pick(f.reviewType),
            // Full IST day so a same-day record isn't excluded by a UTC-midnight boundary
            // (Lighthouse auditors work in IST). dateTo is end-of-day IST.
            dateFrom: startOfDayEpoch(f.startDate),
            dateTo: endOfDayEpoch(f.endDate),
            pageSize: CASE_AUDIT_PAGE_SIZE,       // fixed page size, within the server max of 100
            pageToken                             // omit = first page
        };
    };

    const runSearch = useCallback(async (a: ActivitySearchCriteria, f: QueryCondition,
                                         pageToken?: string) => {
        setLoading(true);
        try {
            const data = await LighthouseApi.searchCases(buildRequest(a, f, pageToken));
            currentTokenRef.current = pageToken;
            setCases(data.cases);
            setFeatures(data.features);
            setAlias(data.alias);
            setNextToken(data.nextToken ?? null);
            setPrevToken(data.prevToken ?? null);
        } catch (error: any) {
            console.error("Error searching cases:", error);
            alert(error.response?.data?.message ||
                ("Error while loading cases: " + error.message + " Please try again later!"));
            setCases([]);
            setNextToken(null);
            setPrevToken(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Index-derived dropdown values, fetched once on mount. undefined = use the static
    // DropDownConfig fallback (also the end state when the fetch fails — page never blocks).
    const [filterOptions, setFilterOptions] = useState<FilterOptionsPayload | undefined>(undefined);

    useEffect(() => {
        runSearch(EMPTY_CRITERIA, caseAuditDefaultFilterValues());
        LighthouseApi.getFilterOptions()
            .then(setFilterOptions)
            .catch(error => console.error("Filter options unavailable, using static lists:", error));
    }, [runSearch]);

    const onActivityChange = (c: ActivitySearchCriteria) => setCriteria(c);
    // A new search is a fresh selection context: the prior result set is gone, so any rows
    // selected against it must be dropped (otherwise the "N selected" count keeps counting
    // cases that are no longer in the results). Paging (onNext/onPrevious) keeps the selection.
    const onSearch = () => {
        setSelectedIds(new Set());
        runSearch(criteria, filters);   // filter change -> first page
    };
    const onResetAll = () => {
        const defaults = caseAuditDefaultFilterValues();
        setCriteria(EMPTY_CRITERIA);
        setFilters(defaults);
        setSelectedIds(new Set());
        setResetKey(k => k + 1);   // remount ActivitySearch to clear its inputs
        runSearch(EMPTY_CRITERIA, defaults);
    };
    const onNext = () => nextToken && runSearch(criteria, filters, nextToken);
    const onPrevious = () => prevToken && runSearch(criteria, filters, prevToken);

    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
    const [viewReviewId, setViewReviewId] = useState<string | null>(null);
    const [viewReadOnly, setViewReadOnly] = useState<boolean>(true);

    const onBulkAction = () => {
        if (selectedIds.size === 0) return;
        setBulkDialogOpen(true);
    };

    const onBulkDialogClose = () => {
        setBulkDialogOpen(false);
    };

    const onBulkSuccess = () => {
        setBulkDialogOpen(false);
        setSelectedIds(new Set());
        runSearch(criteria, filters, currentTokenRef.current);   // refresh the current page
    };

    const onCaseAction = useCallback((action: string, caseId: string) => {
        const found = cases.find(c => c.caseId === caseId);
        if (!found) return;
        if (action === 'VIEW') {
            setViewReviewId(caseId);
            setViewReadOnly(true);
            return;
        }
        if (action === 'REOPEN') {
            setViewReviewId(caseId);
            setViewReadOnly(false);
            return;
        }
        alert(`${action} is not wired yet for case ${caseId}.`);
    }, [cases]);

    const renderOverview = () => {
        return (
            <>
                <div className='case-filters' data-testid='case-filters'>
                    <div className='case-filters-header'><span><b>Search &amp; Filters:</b></span></div>
                    <ActivitySearch key={resetKey} canSearchByLogin={features.canSearchByLogin} alias={alias}
                                    onChange={onActivityChange} onEnter={onSearch}/>
                    <FilterBar value={filters} onChange={setFilters} allowAll dynamicOptions={filterOptions}/>
                    <div className='case-filters-actions'>
                        <KatButton variant='primary' onClick={onSearch} disabled={loading}
                                   data-testid='case-search'>Search</KatButton>
                        <KatButton className='horizontal-margin' onClick={onResetAll} disabled={loading}
                                   data-testid='case-reset'>Reset</KatButton>
                    </div>
                </div>
                {loading ? (
                    <div className={'table-center'}><KatSpinner size="large"/></div>
                ) : (
                    <CaseActivityTable
                        cases={cases}
                        onAction={onCaseAction}
                        onBulkAction={onBulkAction}
                        canBulkAction={features.canBulkNoAction && filters.reviewStatus === 'PENDING'}
                        maxSelection={MAX_SELECTION}
                        showLogin={features.canSearchByLogin}
                        selectedIds={selectedIds}
                        onToggleSelect={onToggleSelect}
                        onToggleAll={onToggleAll}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        canNext={nextToken != null}
                        canPrevious={prevToken != null}
                    />
                )}
            </>
        );
    };

    return (
        <div className='case-audit-container default-margin' data-testid='case-audit-page'>
            {renderOverview()}
            <BulkActionDialog
                visible={bulkDialogOpen}
                selectedIds={selectedIds}
                onClose={onBulkDialogClose}
                onSuccess={onBulkSuccess}
            />
            <CaseViewerModal
                reviewId={viewReviewId || ''}
                visible={!!viewReviewId}
                onClose={() => setViewReviewId(null)}
                readOnly={viewReadOnly}
            />
        </div>
    );
};

export default CaseAudit;
